import { MatchStatus } from '@/types';
import { isoToAlgiersInput } from '@/utils/algiersTime';
import { normalizeWilayaKey } from '@/utils/wilayaLocalization';

/**
 * Propositions d'arbitres (spec docs/superpowers/specs/2026-10-30-referee-designation-proposals-design.md).
 * Logique pure partagée par la page du Responsable Arbitres et la modale de désignation.
 */

/** Query-key prefix of every proposal query (hooks/useRefereeProposals, realtime invalidation). */
export const REFEREE_PROPOSALS_QUERY_KEY_PREFIX = 'referee-proposals';

export type RefereeProposalStatus = 'pending' | 'validated' | 'replaced' | 'refused' | 'withdrawn';

export interface RefereeProposal {
    id: string;
    matchId: string;
    role: string;
    officialId: string;
    leagueId: string;
    season: string;
    status: RefereeProposalStatus;
    replacedByOfficialId: string | null;
    proposedBy: string | null;
    decidedBy: string | null;
    decidedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

/** Row of get_referee_proposal_aids (migration 20261030170000). */
export interface RefereeProposalAid {
    officialId: string;
    seasonMatches: number;
    lastMatchDate: string | null;
    homeTeamMatches: number;
    awayTeamMatches: number;
    nearestOtherDate: string | null;
    officialWilaya: string | null;
    homeWilaya: string | null;
    awayWilaya: string | null;
    /** Central of the slot's match (pending proposal, else designated); null when none. */
    centralOfficialId: string | null;
    /** Matches this season on the same crew as that central. */
    withCentralMatches: number;
}

/** Row of get_referee_usual_trios: a central whose same pair covers ≥ 70 % of his matches. */
export interface UsualTrio {
    centralId: string;
    assistant1Id: string;
    assistant2Id: string;
    pairMatches: number;
    centralMatches: number;
    season: string;
}

/** Warn when another designation / pending proposal is this many days away or less. */
export const REST_DAYS_WARNING = 2;

export const proposalSlotKey = (matchId: string, role: string) => `${matchId}|${role}`;

const decidedTime = (p: RefereeProposal) => Date.parse(p.decidedAt ?? p.updatedAt) || 0;

/**
 * The proposal shown on each slot: the pending one if any, else the most recently
 * decided one (so he sees « Validée » / « Remplacée » / « Refusée »). A withdrawn
 * proposal shows nothing: the slot is back to empty.
 */
export function slotProposals(proposals: RefereeProposal[]): Map<string, RefereeProposal> {
    const out = new Map<string, RefereeProposal>();
    for (const p of proposals) {
        if (p.status === 'withdrawn') continue;
        const key = proposalSlotKey(p.matchId, p.role);
        const cur = out.get(key);
        if (!cur) {
            out.set(key, p);
        } else if (cur.status !== 'pending' && (p.status === 'pending' || decidedTime(p) > decidedTime(cur))) {
            out.set(key, p);
        }
    }
    return out;
}

/**
 * Slots each official would hold in the round: a pending proposal stands for its
 * slot (it is what he is planning), otherwise the league's designation.
 */
export function roundLoadByOfficial(
    matches: Array<{ id: string; assignments: Array<{ role: string; officialId: string | null }> }>,
    proposals: RefereeProposal[],
): Map<string, number> {
    const pending = new Map<string, string>();
    for (const p of proposals) if (p.status === 'pending') pending.set(proposalSlotKey(p.matchId, p.role), p.officialId);
    const load = new Map<string, number>();
    const add = (id: string | null | undefined) => {
        if (id) load.set(id, (load.get(id) ?? 0) + 1);
    };
    const seen = new Set<string>();
    for (const m of matches) {
        for (const a of m.assignments) {
            const key = proposalSlotKey(m.id, a.role);
            seen.add(key);
            add(pending.has(key) ? pending.get(key) : a.officialId);
        }
    }
    for (const [key, officialId] of pending) if (!seen.has(key)) add(officialId);
    return load;
}

const dayDiff = (a: string, b: string) => Math.round(Math.abs(Date.parse(a) - Date.parse(b)) / 86_400_000);

export interface ProposalWarnings {
    /** Days to the nearest other match, when ≤ REST_DAYS_WARNING; else null. */
    restDays: number | null;
    /** Official's wilaya equals one of the two clubs'. */
    sameWilaya: boolean;
    /** Which club shares it (home first). */
    sameWilayaSide: 'home' | 'away' | null;
}

export function proposalWarnings(aid: RefereeProposalAid | undefined, matchDate: string | null): ProposalWarnings {
    if (!aid) return { restDays: null, sameWilaya: false, sameWilayaSide: null };
    const diff = matchDate && aid.nearestOtherDate ? dayDiff(aid.nearestOtherDate, matchDate) : null;
    const own = normalizeWilayaKey(aid.officialWilaya);
    const sameWilayaSide = !own
        ? null
        : normalizeWilayaKey(aid.homeWilaya) === own
          ? 'home'
          : normalizeWilayaKey(aid.awayWilaya) === own
            ? 'away'
            : null;
    return {
        restDays: diff !== null && diff <= REST_DAYS_WARNING ? diff : null,
        sameWilaya: sameWilayaSide !== null,
        sameWilayaSide,
    };
}

const ERROR_KEYS: Record<string, string> = {
    PROPOSAL_FORBIDDEN: 'forbidden',
    PROPOSAL_MATCH_LOCKED: 'match_locked',
    PROPOSAL_NOT_REFEREE_ROLE: 'not_referee_role',
    OFFICIAL_NOT_LEAGUE_MEMBER: 'not_member',
    PROPOSAL_ALREADY_DESIGNATED: 'already_designated',
    PROPOSAL_OFFICIAL_ON_OTHER_SLOT: 'official_on_other_slot',
    PROPOSAL_ALREADY_CLOSED: 'already_closed',
    PROPOSAL_MATCH_NOT_FOUND: 'match_not_found',
};

/** i18n key (namespace « planning ») for an error raised by the proposal RPCs. */
export function proposalErrorKey(error: { message?: string } | null | undefined): string {
    const message = error?.message ?? '';
    const code = Object.keys(ERROR_KEYS).find((c) => message.includes(c));
    return `referee_proposals.errors.${code ? ERROR_KEYS[code] : 'generic'}`;
}

/** '2026-2027' → '2025-2026'; null for any other shape. */
export function previousSeason(season: string): string | null {
    const m = /^(\d{4})-(\d{4})$/.exec(season);
    return m ? `${Number(m[1]) - 1}-${Number(m[2]) - 1}` : null;
}

/** This season's usual trio of a central, else last season's. */
export function usualTrioFor(centralId: string | null, current: UsualTrio[], previous: UsualTrio[]): UsualTrio | null {
    if (!centralId) return null;
    return current.find((t) => t.centralId === centralId) ?? previous.find((t) => t.centralId === centralId) ?? null;
}

/**
 * Assistant slots to propose for a trio: `holders` = who stands in each slot now
 * (pending proposal, else designation). Slots already holding the trio's assistant are skipped.
 */
export function trioToPropose(
    trio: UsualTrio,
    assistantRoles: readonly [string, string],
    holders: Record<string, string | null>,
): Array<{ role: string; officialId: string }> {
    const wanted = [trio.assistant1Id, trio.assistant2Id];
    return assistantRoles
        .map((role, i) => ({ role, officialId: wanted[i] }))
        .filter(({ role, officialId }) => holders[role] !== officialId);
}

/**
 * Only upcoming matches take designations from the commission d'arbitrage: « Prévu » or
 * « Reporté », and kick-off (Algiers wall time) not reached. Played, live and cancelled
 * matches are read-only. Same rule as propose_referee (PROPOSAL_MATCH_LOCKED).
 */
export function isOpenForDesignation(
    match: { status: MatchStatus; matchDate: string | null; matchTime: string | null },
    now: Date = new Date(),
): boolean {
    if (match.status !== MatchStatus.SCHEDULED && match.status !== MatchStatus.POSTPONED) return false;
    if (!match.matchDate) return true;
    const [today, clock] = isoToAlgiersInput(now.toISOString()).split('T');
    if (match.matchDate !== today) return match.matchDate > today;
    return !match.matchTime || match.matchTime.slice(0, 5) > clock;
}
